import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  Image,
  Layers,
  User,
  LogOut,
  Plus,
  Trash2,
  Edit,
  Upload,
  X,
  BookOpen,
  Crop,
  ShoppingBag,
  ExternalLink
} from 'lucide-react';
import { getApiUrl, makeRequest, resolveImageUrl } from '../../utils/api';
import ImageCropper from '../../components/ImageCropper';
import '../../styles/admin.css';

const AdminStorefront = () => {
  const { token, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [storefrontUrl, setStorefrontUrl] = useState('');
  const [macUrl, setMacUrl] = useState('');
  const [aeUrl, setAeUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropExistingImage, setCropExistingImage] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    note: '',
    image: '',
    amazon_link: '',
    order: 0
  });

  const fetchData = useCallback(async () => {
    try {
      const apiUrl = getApiUrl();
      const [productsRes, businessRes] = await Promise.all([
        makeRequest('GET', `${apiUrl}/api/storefront-products`),
        makeRequest('GET', `${apiUrl}/api/business-info`)
      ]);

      if (productsRes.ok) setProducts(productsRes.data);
      if (businessRes.ok) {
        setBusinessInfo(businessRes.data);
        setStorefrontUrl(businessRes.data.amazon_storefront_url || '');
        setMacUrl(businessRes.data.mac_storefront_url || '');
        setAeUrl(businessRes.data.ae_storefront_url || '');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveStorefrontUrl = async () => {
    setSavingUrl(true);
    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest(
        'PUT',
        `${apiUrl}/api/business-info`,
        {
          ...businessInfo,
          amazon_storefront_url: storefrontUrl,
          mac_storefront_url: macUrl,
          ae_storefront_url: aeUrl
        },
        { Authorization: `Bearer ${token}` }
      );
      if (result.ok) {
        alert('Storefront URLs saved!');
        setBusinessInfo(result.data);
      } else {
        alert('Error saving URLs');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
    setSavingUrl(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest(
        'POST',
        `${apiUrl}/api/upload/image`,
        uploadData,
        { Authorization: `Bearer ${token}` },
        true
      );
      if (result.ok) {
        setFormData((prev) => ({ ...prev, image: result.data.url }));
      } else {
        alert('Upload error: ' + (result.data.detail || 'Unknown'));
      }
    } catch (error) {
      alert('Upload error: ' + error.message);
    }
    setUploading(false);
  };

  const handleCroppedImageUpload = async (croppedBlob) => {
    setUploading(true);
    setShowCropper(false);
    setCropExistingImage(null);
    const uploadData = new FormData();
    uploadData.append('file', croppedBlob, 'cropped-image.jpg');

    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest(
        'POST',
        `${apiUrl}/api/upload/image`,
        uploadData,
        { Authorization: `Bearer ${token}` },
        true
      );
      if (result.ok) {
        setFormData((prev) => ({ ...prev, image: result.data.url }));
      } else {
        alert('Upload error: ' + (result.data.detail || 'Unknown'));
      }
    } catch (error) {
      alert('Upload error: ' + error.message);
    }
    setUploading(false);
  };

  const handleCropExisting = () => {
    setCropExistingImage(resolveImageUrl(formData.image));
    setShowCropper(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const apiUrl = getApiUrl();
    const url = editingItem
      ? `${apiUrl}/api/storefront-products/${editingItem.id}`
      : `${apiUrl}/api/storefront-products`;
    const method = editingItem ? 'PUT' : 'POST';

    try {
      const result = await makeRequest(method, url, formData, {
        Authorization: `Bearer ${token}`
      });
      if (result.ok) {
        setShowModal(false);
        setEditingItem(null);
        setFormData({ name: '', note: '', image: '', amazon_link: '', order: 0 });
        fetchData();
      } else {
        alert('Error: ' + (result.data.detail || 'Unknown'));
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      note: item.note || '',
      image: item.image || '',
      amazon_link: item.amazon_link || '',
      order: item.order || 0
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest(
        'DELETE',
        `${apiUrl}/api/storefront-products/${id}`,
        null,
        { Authorization: `Bearer ${token}` }
      );
      if (result.ok) fetchData();
      else alert('Error deleting');
    } catch (error) {
      alert('Error deleting');
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      note: '',
      image: '',
      amazon_link: '',
      order: products.length
    });
    setShowModal(true);
  };

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <h2>The Slay Station</h2>
          <p>Admin Panel</p>
        </div>

        <nav className="admin-nav">
          <Link to="/admin/dashboard" className="admin-nav-item">
            <LayoutDashboard size={20} />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/portfolio" className="admin-nav-item">
            <Image size={20} />
            <span>Portfolio</span>
          </Link>
          <Link to="/admin/services" className="admin-nav-item">
            <Layers size={20} />
            <span>Services</span>
          </Link>
          <Link to="/admin/storefront" className="admin-nav-item active">
            <ShoppingBag size={20} />
            <span>Curated Favorites</span>
          </Link>
          <Link to="/admin/business" className="admin-nav-item">
            <User size={20} />
            <span>Bio & Info</span>
          </Link>
        </nav>

        <div className="admin-sidebar-footer">
          <Link to="/" className="admin-nav-item" target="_blank">
            <BookOpen size={20} />
            <span>View Site</span>
          </Link>
          <button onClick={logout} className="admin-nav-item logout">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-header">
          <div>
            <h1>Curated Favorites</h1>
            <p>Manage your Amazon storefront products & link</p>
          </div>
          <button onClick={openAddModal} className="admin-btn-primary" data-testid="add-product-btn">
            <Plus size={20} />
            Add Product
          </button>
        </header>

        <div className="admin-content">
          {/* Storefront URLs Section */}
          <div className="admin-section-card" style={{ marginBottom: 24 }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ShoppingBag size={20} />
              Storefront Links
            </h2>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              These links appear on the homepage and the Curated Favorites page.
            </p>

            <div className="admin-form-group">
              <label>Amazon Storefront URL</label>
              <input
                type="url"
                value={storefrontUrl}
                onChange={(e) => setStorefrontUrl(e.target.value)}
                placeholder="https://www.amazon.com/shop/yourname"
                data-testid="storefront-url-input"
              />
            </div>

            <div className="admin-form-group">
              <label>MAC Cosmetics Storefront URL</label>
              <input
                type="url"
                value={macUrl}
                onChange={(e) => setMacUrl(e.target.value)}
                placeholder="https://community.maccosmetics.com/a/yourname"
                data-testid="mac-url-input"
              />
            </div>

            <div className="admin-form-group">
              <label>American Eagle Ambassador URL</label>
              <input
                type="url"
                value={aeUrl}
                onChange={(e) => setAeUrl(e.target.value)}
                placeholder="https://www.ae.com/..."
                data-testid="ae-url-input"
              />
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                onClick={handleSaveStorefrontUrl}
                disabled={savingUrl}
                className="admin-btn-primary"
                data-testid="save-storefront-url-btn"
              >
                {savingUrl ? 'Saving...' : 'Save URLs'}
              </button>
              {storefrontUrl && (
                <a
                  href={storefrontUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="admin-btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
                >
                  <ExternalLink size={16} /> Test Amazon
                </a>
              )}
              {macUrl && (
                <a
                  href={macUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="admin-btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
                >
                  <ExternalLink size={16} /> Test MAC
                </a>
              )}
              {aeUrl && (
                <a
                  href={aeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="admin-btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
                >
                  <ExternalLink size={16} /> Test AE
                </a>
              )}
            </div>
          </div>

          {/* Products List */}
          {loading ? (
            <div className="admin-loading">Loading...</div>
          ) : products.length === 0 ? (
            <div className="admin-empty">
              <ShoppingBag size={48} />
              <h3>No products yet</h3>
              <p>Add your first curated product to get started</p>
              <button onClick={openAddModal} className="admin-btn-primary">
                <Plus size={20} />
                Add First Product
              </button>
            </div>
          ) : (
            <div className="admin-portfolio-grid">
              {products.map((p) => (
                <div key={p.id} className="admin-portfolio-card">
                  <div className="admin-portfolio-image">
                    {p.image ? (
                      <img src={resolveImageUrl(p.image)} alt={p.name} />
                    ) : (
                      <div
                        style={{
                          height: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f1ead9',
                          color: '#b8860b'
                        }}
                      >
                        <ShoppingBag size={40} />
                      </div>
                    )}
                  </div>
                  <div className="admin-portfolio-info">
                    <h4>{p.name}</h4>
                    {p.note && <p>{p.note}</p>}
                    <a
                      href={p.amazon_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: 12,
                        color: '#b8860b',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        marginTop: 6
                      }}
                    >
                      <ExternalLink size={12} /> Amazon link
                    </a>
                  </div>
                  <div className="admin-portfolio-actions">
                    <button
                      onClick={() => handleEdit(p)}
                      className="admin-btn-icon"
                      data-testid={`edit-product-${p.id}`}
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(p.id)}
                      className="admin-btn-icon delete"
                      data-testid={`delete-product-${p.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingItem ? 'Edit Product' : 'Add Product'}</h2>
              <button
                onClick={() => setShowModal(false)}
                className="admin-modal-close"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g., Glow Setting Spray"
                  required
                  data-testid="product-name-input"
                />
              </div>

              <div className="admin-form-group">
                <label>Short Note (optional)</label>
                <textarea
                  value={formData.note}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, note: e.target.value }))
                  }
                  placeholder="Why you love it..."
                  rows={2}
                  data-testid="product-note-input"
                />
              </div>

              <div className="admin-form-group">
                <label>Amazon Link *</label>
                <input
                  type="url"
                  value={formData.amazon_link}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      amazon_link: e.target.value
                    }))
                  }
                  placeholder="https://www.amazon.com/..."
                  required
                  data-testid="product-amazon-link-input"
                />
              </div>

              <div className="admin-form-group">
                <label>Product Image</label>
                <div className="admin-image-upload">
                  {formData.image ? (
                    <div className="admin-image-preview">
                      <img src={resolveImageUrl(formData.image)} alt="Preview" />
                      <div className="admin-image-actions">
                        <button
                          type="button"
                          onClick={handleCropExisting}
                          className="admin-image-crop"
                          title="Crop Image"
                        >
                          <Crop size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({ ...prev, image: '' }))
                          }
                          className="admin-image-remove"
                          title="Remove Image"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="admin-upload-options-compact">
                      <button
                        type="button"
                        onClick={() => {
                          setCropExistingImage(null);
                          setShowCropper(true);
                        }}
                        className="admin-crop-upload-btn-small"
                        disabled={uploading}
                      >
                        <Crop size={18} />
                        <span>Crop & Upload</span>
                      </button>
                      <label className="admin-upload-btn-small">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          hidden
                        />
                        {uploading ? (
                          <span>Uploading...</span>
                        ) : (
                          <>
                            <Upload size={18} />
                            <span>Quick Upload</span>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                </div>
                <p className="admin-form-hint">Or paste an image URL:</p>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, image: e.target.value }))
                  }
                  placeholder="https://..."
                />
              </div>

              <div className="admin-form-actions">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="admin-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-primary"
                  data-testid="save-product-btn"
                >
                  {editingItem ? 'Save Changes' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCropper && (
        <ImageCropper
          onCropComplete={handleCroppedImageUpload}
          onCancel={() => {
            setShowCropper(false);
            setCropExistingImage(null);
          }}
          existingImageUrl={cropExistingImage}
          parentToken={token}
        />
      )}
    </div>
  );
};

export default AdminStorefront;
